import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Robot, WarningCircle } from "@phosphor-icons/react";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { adminApi, getErrorMessage } from "@/lib/api";
import { fmtDate } from "./parts";

function Stats({ ov }) {
  const st = ov.auto_stats || {};
  return (
    <div className="text-[0.7rem] text-neutral-500 mt-2 space-y-1" data-testid="wi-auto-stats">
      <p>{st.approved || 0} approved by the AI{st.last ? ` · last ${st.last.otma} ${fmtDate(st.last.finished_at)}` : ""}
        {st.failed ? <span className="text-red-400"> · {st.failed} need attention on <Link to="/admin/warranty-intel/approvals" className="underline">Approvals</Link></span> : null}</p>
      {!!st.skipped?.length && <details><summary className="cursor-pointer text-neutral-400" data-testid="wi-auto-skipped-toggle">Recently left for a person ({st.skipped.length})</summary>
        <ul className="mt-1 space-y-0.5">{st.skipped.map((x) => <li key={x.claim_id}>{x.claim?.customer || x.claim?.chassis || "Claim"}: {x.auto_approval?.reason}</li>)}</ul></details>}
    </div>
  );
}

function Confirm({ open, setOpen, ov, onYes, busy }) {
  const t = ov.settings || {};
  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogContent className="bg-[#0F0F0F] border-neutral-800 text-neutral-200" data-testid="wi-auto-approve-confirm">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-white">Let the AI approve claims?</AlertDialogTitle>
          <AlertDialogDescription className="text-neutral-400">
            From now on, new claims the AI marks as eligible are approved straight away, exactly like pressing Approve. That's {t.confidence_auto_approve}%+ confidence, up to ${Number(t.auto_approve_limit_aud || 0).toLocaleString("en-AU")} inc GST, no blockers and no possible duplicates.
            The approval number is allocated, Monday.com and the Register are updated, and the emails follow your Outlook setting. Recorded under {ov.auto_approve_as?.name}. Claims that are already open still need a person.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="bg-transparent border-neutral-700 text-neutral-300 hover:bg-neutral-900 hover:text-white" data-testid="wi-auto-approve-cancel">Not yet</AlertDialogCancel>
          <AlertDialogAction className="bg-[#73bf44] text-black hover:bg-[#8ad35c]" disabled={busy} onClick={(e) => { e.preventDefault(); onYes(); }} data-testid="wi-auto-approve-confirm-btn">Turn on</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function AutoApproveStep({ ov, onChange, busy: outerBusy }) {
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [ask, setAsk] = useState(false);
  useEffect(() => { adminApi.get("/admin/wi/monday-users").then((r) => setUsers(r.data.users || [])).catch(() => {}); }, []);
  const put = async (patch, msg) => {
    setBusy(true);
    try { onChange((await adminApi.put("/admin/wi/assess/activation", patch)).data); toast.success(msg); }
    catch (e) { toast.error(getErrorMessage(e)); } finally { setBusy(false); setAsk(false); }
  };
  const on = ov.auto_approvals_enabled;
  const why = !ov.validated ? "Validate the historical database first" : !ov.ai_assessment_enabled ? "Switch on step 2 first" : !ov.auto_approve_as ? "Choose who approvals are recorded under" : null;
  return (
    <div data-testid="wi-auto-approve-step">
      <label className="flex items-center justify-between gap-3 text-sm text-neutral-200">
        <span className="flex items-center gap-1.5">{on ? <><Robot size={15} className="text-[#73bf44]" /> On · AI approves eligible claims</> : "Off · ready when you are"}</span>
        <Switch checked={on} disabled={busy || outerBusy || (!on && !!why)} onCheckedChange={(v) => (v ? setAsk(true) : put({ auto_approvals_enabled: false }, "AI approvals switched off. A person approves every claim"))} data-testid="wi-auto-approve-toggle" />
      </label>
      <label className="block mt-2 text-[0.7rem] text-neutral-500">Record AI approvals under
        <select value={ov.auto_approve_as?.id || ""} disabled={busy} onChange={(e) => put({ auto_approve_as_id: e.target.value }, "Saved")} data-testid="wi-auto-approve-as"
          className="mt-1 w-full bg-black/50 border border-neutral-800 rounded-lg px-2.5 py-1.5 text-sm text-white outline-none focus:border-[#73bf44] transition-colors">
          <option value="" disabled>Choose a Monday.com person…</option>
          {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          {ov.auto_approve_as && !users.some((u) => u.id === ov.auto_approve_as.id) && <option value={ov.auto_approve_as.id}>{ov.auto_approve_as.name}</option>}
        </select></label>
      <p className="text-[0.7rem] text-neutral-500 mt-1.5">{on ? `On since ${fmtDate(ov.auto_approvals_since)}. Only claims that arrived after that are approved by the AI.` : "When it's on, new claims the AI marks as eligible are approved automatically. Everything else still waits on the Approvals page."}</p>
      {!on && why && <p className="text-[0.7rem] text-amber-300/80 mt-1 flex items-center gap-1" data-testid="wi-auto-approve-why"><WarningCircle size={13} /> {why}</p>}
      {on && !ov.ai_assessment_enabled && <p className="text-[0.7rem] text-amber-300 mt-1" data-testid="wi-auto-approve-paused">Paused: AI checks (step 2) are off, so nothing is being approved.</p>}
      {on && <Stats ov={ov} />}
      <Confirm open={ask} setOpen={setAsk} ov={ov} busy={busy} onYes={() => put({ auto_approvals_enabled: true }, "AI approvals switched on")} />
    </div>
  );
}
